import {isArray, isUndefined} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Renderer, RendererOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {Skeleton} from '../../skeleton';
import {getCellBackgroundColor, getCellIndexStyles} from '../helpers/tableCellHelpers';
import {getWidthSettingsStyles, TableColumnSettings} from '../tableColumn';
import {EmptyCell} from './emptyCell';
import {MaybeRenderStickyContainerCell} from './stickyContainerCell';

/*
 * Types.
 */

export interface TableCellProps<S> {
  index: number;
  value?: S;
  isLoading?: boolean;
  isSelected?: boolean;
  isHovering?: boolean;
  settings?: TableColumnSettings;

  size?: VisualSizesEnum;

  shouldShowStickyGradient?: boolean;
  /** These two flags should be only set to remove the radius between selected items */
  shouldRemoveTopBorderRadius?: boolean;
  shouldRemoveBottomBorderRadius?: boolean;
  /** This flag is to permanently remove the left border radius for a cell */
  shouldRemoveLeftBorderRadius?: boolean;
}

interface TableCellContentsProps {
  settings?: TableColumnSettings;
  isSelected?: boolean;
  isHovering?: boolean;
}

export interface TableCellPropsWithAvatar<S> extends TableCellProps<S> {
  avatarSize?: VisualSizesEnum;
}

export interface TableCellStyleProps {
  $index: number;
  $isHovering: boolean;
  $isSelected: boolean;
  $settings?: TableColumnSettings;
  $shouldRemoveTopBorderRadius?: boolean;
  $shouldRemoveBottomBorderRadius?: boolean;
  $shouldRemoveLeftBorderRadius?: boolean;
}

interface RenderTableCellProps<S> extends TableCellProps<S> {
  renderLoading?: Renderer;
  renderCell: RendererOf<S>;
  renderEmptyState?: RendererOf<S | undefined>;
}

/*
 * Styles.
 */

const StyledTableCellGenericLoadingContainerDiv = styled.div<TableCellStyleProps>`
  padding: 0 12px;
  display: flex;
  overflow: hidden;
  white-space: nowrap;
  align-items: center;
  height: 100%;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)}
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
    )}
`;

const StyledMaxWidthContainerDiv = styled.div`
  display: flex;
  flex-flow: row;
  height: 100%;
`;
interface StyledMaxWidthContentsDivProps {
  $maxWidth: number;
}
const StyledMaxWidthContentsDiv = styled.div<StyledMaxWidthContentsDivProps>`
  max-width: ${(p) => p.$maxWidth}px;
`;

interface StyledFillerDivProps {
  $isHovering: boolean;
  $isSelected: boolean;
}
const StyledFillerDiv = styled.div<StyledFillerDivProps>`
  flex: 1;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
`;

/*
 * Component.
 */

export const TableCell = <S extends {}>(props: RenderTableCellProps<S>) => {
  const {
    index,
    value,
    isLoading,
    isHovering,
    isSelected,
    settings,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
    renderLoading,
    renderCell,
    renderEmptyState,
  } = props;

  if (isLoading) {
    return (
      <MaybeRenderStickyContainerCell
        settings={settings}
        index={index}
        shouldShowStickyGradient={shouldShowStickyGradient}
      >
        <TableCellContents settings={settings} isHovering={isHovering} isSelected={isSelected}>
          {renderLoading ? (
            renderLoading()
          ) : (
            <StyledTableCellGenericLoadingContainerDiv
              $settings={settings}
              $isHovering={false}
              $isSelected={false}
              $index={index}
              $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
              $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
            >
              <Skeleton height="16px" borderRadius="4px" scheme={settings?.loading?.style} />
            </StyledTableCellGenericLoadingContainerDiv>
          )}
        </TableCellContents>
      </MaybeRenderStickyContainerCell>
    );
  }

  if (
    isUndefined(value) ||
    (typeof value === 'string' && value.trim() === '') ||
    (isArray(value) && value.length === 0)
  ) {
    if (!renderEmptyState) {
      return (
        <EmptyCell
          settings={settings}
          isHovering={isHovering}
          index={index}
          isSelected={isSelected}
          shouldShowStickyGradient={shouldShowStickyGradient}
          shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        />
      );
    }
    return (
      <MaybeRenderStickyContainerCell
        settings={settings}
        index={index}
        shouldShowStickyGradient={shouldShowStickyGradient}
      >
        <TableCellContents settings={settings} isHovering={isHovering} isSelected={isSelected}>
          {renderEmptyState(value)}
        </TableCellContents>
      </MaybeRenderStickyContainerCell>
    );
  }
  return (
    <MaybeRenderStickyContainerCell
      settings={settings}
      index={index}
      shouldShowStickyGradient={shouldShowStickyGradient}
    >
      <TableCellContents settings={settings} isHovering={isHovering} isSelected={isSelected}>
        {renderCell(value)}
      </TableCellContents>
    </MaybeRenderStickyContainerCell>
  );
};

const TableCellContents: FC<TableCellContentsProps> = (props) => {
  const {settings, children, isHovering, isSelected} = props;

  // If we should fill the remaining space and have a max width set, we need to wrap the contents
  if (settings?.cellWidth?.shouldFillRemainingWidth && settings.cellWidth.maxWidth) {
    return (
      <StyledMaxWidthContainerDiv>
        <StyledMaxWidthContentsDiv $maxWidth={settings.cellWidth.maxWidth}>
          {children}
        </StyledMaxWidthContentsDiv>
        <StyledFillerDiv $isHovering={Boolean(isHovering)} $isSelected={Boolean(isSelected)} />
      </StyledMaxWidthContainerDiv>
    );
  }
  return <>{children}</>;
};
